import { useRef } from 'react'

import { useMutation } from '@tanstack/react-query'
import { UseNavigateResult } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { handleFormSubmissionError } from 'ui/components'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'
import { DocTemplate } from '~bank-document-templates/types'

import { TemplateManageFormApi } from '../context'

export const useSaveTemplate = ({
  formApi,
  navigate,
  returnUrl,
  onSuccess
}: {
  formApi: TemplateManageFormApi
  navigate: UseNavigateResult<any>
  returnUrl?: string
  onSuccess: () => void
}) => {
  const templateId = useRef<number | undefined>(undefined)

  const confirmation = useConfirmation()

  const { mutateAsync } = useMutation<{ id?: number }, Error, DocTemplate>({
    mutationFn: (template) =>
      template.id
        ? Request.put(Request.urls.bank.documentTemplate, template)
        : Request.post(Request.urls.bank.documentTemplate, template),
    onError: (error) => {
      handleFormSubmissionError<DocTemplate>(error, formApi)
    },
    onSuccess: (value) => {
      templateId.current = value?.id
      onSuccess()
    }
  })

  const openCreateConfirmation = async (value: DocTemplate) => {
    if (
      await confirmation.showAskConfirmation({
        message: `Are you sure you want to ${value.id ? 'save' : 'create'} the Template ${value.name}? `,
        submitButtonText: `${value.id ? 'Save' : 'Create'}`,
        confirmCallback: () => mutateAsync(value)
      })
    ) {
      await confirmation.showDoneConfirmation({
        message: `The Template ${value.name} was ${value.id ? 'saved' : 'created'} successfully`
      })

      const id = templateId.current || value.id

      returnUrl
        ? navigate({ to: returnUrl })
        : id
          ? navigate({ to: routes.bank.documentTemplates.details, params: { id: id.toString() } })
          : navigate({ to: routes.bank.documentTemplates.index })

      templateId.current = undefined
    }
  }

  return {
    openCreateConfirmation
  }
}
